import styled, { css } from 'styled-components';

import { Text } from '../../../../components';
import { FTUX_GRADIENTS, FTUX_PICK } from '../../../../theme/ftux-tokens';
import { ANIM, COLORS, LUMIO, SPACE } from '../../../../theme/tokens';
import { cardWidth } from './Spotlight.styles';

/* "Lumio's pick" (Figma Owner Studio 9835:93603), the ows-ftux prototype's Spotlight pick, sizes included. */

/** The pick's corners, a little rounder than the section's former 16px (the user, 2026-10-14). */
const PICK_RADIUS = '20px';

/**
 * Its border stays the card's own; the waiting shine only laps it. The surface is dark frosted, deepening from the
 * card's colour to near-black toward its far corner (white to the light inset in light, FTUX_PICK), under a quieter
 * hairline; the page behind deeply blurred. The card itself is no control (only its buttons are): no hover wash, no
 * pressed look (the user, 2026-10-23: a click on it must not light it as if it did something).
 */
export const Pick = styled.div`
  position: relative;
  isolation: isolate;
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${SPACE[16]};
  padding: ${SPACE[16]};
  border: 1px solid color-mix(in srgb, ${COLORS.hoverBorderLit} 45%, transparent);
  border-radius: ${PICK_RADIUS};
  background: ${FTUX_GRADIENTS.pickSurface};
  backdrop-filter: blur(24px) saturate(105%);
  -webkit-backdrop-filter: blur(24px) saturate(105%);
  color: ${FTUX_PICK.text};

  /* Every card's width (Spotlight.styles): a score card's, never narrower than its title and its buttons. */
  ${cardWidth}
`;

export const PickHeading = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
`;

/** "Getting started" in capitals, as the piece's pop-up tags it: the card's own size and grey, a little tracking so
 *  the capitals breathe. */
export const PickEyebrow = styled(Text)`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.04em;
  color: ${FTUX_PICK.eyebrow};
  text-transform: uppercase;
`;

/**
 * Every card's title, the pick's and each notice's (the user, 2026-10-23): 18px type, two lines at most, never cut
 * after the first; its lines' height (room for its descenders).
 */
export const TITLE_FONT_PX = 18;
export const TITLE_MAX_LINES = 2;
export const TITLE_LINE_PX = 24;
/** The lines every card's title keeps room for (one, or two), set on the strip by Spotlight as it measures them. */
export const TITLE_LINES_VAR = '--spotlight-title-lines';

/** A title's own lines: it wraps, two at most, a longer one ending in an ellipsis (the whole of it in its tooltip). */
export const titleLines = css`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${TITLE_MAX_LINES};
  line-clamp: ${TITLE_MAX_LINES};
  line-height: ${TITLE_LINE_PX}px;
`;

/**
 * A card's room for its title (`data-card="title"`, the title its one child), the same on every card: as many lines as
 * the strip's longest title takes, so the lines and buttons stay level; a shorter title leaves its second line blank,
 * and with every title on one line there is none.
 */
export const TitleSlot = styled.div`
  min-width: 0;
  min-height: calc(var(${TITLE_LINES_VAR}, 1) * ${TITLE_LINE_PX}px);
`;

/** The pick's title, wrapped as every card's (titleLines). */
export const PickTitle = styled(Text)`
  ${titleLines}
  font-size: ${TITLE_FONT_PX}px;
  font-weight: 500;
  color: ${FTUX_PICK.text};
`;

/** The button and Dismiss, on one row whatever the width (the card keeps one height): a long label gives way first,
 *  cut with an ellipsis; Dismiss keeps its size. */
export const PickActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[16]};
  min-width: 0;
`;

/** The button's label, cut with an ellipsis when the row runs short. */
export const PickCtaLabel = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** The pick's button: the studio's primary (orange), as every main action on Home; its label alone. Border-box, so a
 *  one-verb card's span CTA is the button's 40px too. */
export const PickCta = styled.button`
  box-sizing: border-box;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
  height: 40px;
  padding: ${SPACE[0]} ${SPACE[16]};
  border: 1px solid ${COLORS.ctaSolid};
  border-radius: 9999px;
  background: ${COLORS.ctaSolid};
  color: ${COLORS.ctaSolidText};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  transition: filter ${ANIM.INTERACT_MS}ms ease;

  &:hover {
    filter: brightness(1.12);
  }

  &:focus-visible {
    outline: 2px solid ${LUMIO.link};
    outline-offset: 2px;
  }
`;

/** A tertiary beside the pick's primary, as Lumio's card's tertiaries: no fill or border at rest, the card's own ink,
 *  a faint wash on hover; the CTA's height, so the row stays even. */
export const PickDismiss = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  height: 40px;
  padding: ${SPACE[0]} ${SPACE[16]};
  border: 1px solid transparent;
  border-radius: 9999px;
  background: transparent;
  color: ${FTUX_PICK.text};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background ${ANIM.INTERACT_MS}ms ease,
    color ${ANIM.INTERACT_MS}ms ease;

  &:hover {
    background: color-mix(in srgb, ${COLORS.textStrong} 7%, transparent);
    color: ${COLORS.textStrong};
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: 2px solid ${LUMIO.link};
    outline-offset: 2px;
  }
`;
